import { Button, DetectionZone, Device, Facility, FacilityLayoutConfiguration, Icons, LaneLine, ScreenLine, Vector2 } from "@app/shared";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { toastError, toastSuccess } from "../../../helpers";
import { getFacilityCoordsHelper } from "../../../helpers/coordsHelpers";
import { Input, NumericInput } from "../../shared";
import { LayoutDesigner, LayoutDesignerMethods } from "../../shared/LayoutDesigner/LayoutDesigner";
import { RequiresGoogleMaps } from "../../shared/maps/RequiresGoogleMaps";
import {
    averageLatLon,
    createFacility,
    DEVICE_EXPORT_ZOOM,
    loadDraftFromStorage,
    METERS_TO_FEET,
    PlannerDraft,
    parseKmlLocations,
    readKmlText,
    sanitizeFileName,
    saveDraftToStorage,
    toLayoutConfiguration,
    waitMs,
} from "./salesLayoutPlannerHelpers";

export const SalesLayoutPlanner: FC = () => {
    const [draft, setDraft] = useState<PlannerDraft>(loadDraftFromStorage);
    const [isExporting, setIsExporting] = useState(false);

    const designerRef = useRef<LayoutDesignerMethods>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);

    const locationReady = typeof draft.latitude === "number" && typeof draft.longitude === "number";

    const hasAnyLayoutItems = useMemo(
        () =>
            (draft.facility.devices?.length ?? 0) > 0 ||
            (draft.facility.detectionZones?.length ?? 0) > 0 ||
            (draft.facility.screenLines?.length ?? 0) > 0 ||
            (draft.facility.laneLines?.length ?? 0) > 0,
        [draft.facility]
    );

    // ── Draft helpers ────────────────────────────────────────────────

    const replaceDraft = useCallback((next: PlannerDraft) => {
        setDraft(next);
        saveDraftToStorage(next);
    }, []);

    const updateLocation = useCallback(
        (field: "locationName" | "latitude" | "longitude", value: string | number | undefined) => {
            const next: PlannerDraft = { ...draft, [field]: value };
            next.facility = createFacility({
                locationName: next.locationName,
                latitude: next.latitude,
                longitude: next.longitude,
                devices: draft.facility.devices,
                detectionZones: draft.facility.detectionZones,
                screenLines: draft.facility.screenLines,
                laneLines: draft.facility.laneLines,
            });
            replaceDraft(next);
        },
        [draft, replaceDraft]
    );

    const onLayoutSaved = useCallback(
        (config: FacilityLayoutConfiguration) => {
            const nextFacility = new Facility({
                ...draft.facility,
                devices: config.devices ?? [],
                detectionZones: config.detectionZones ?? [],
                screenLines: config.screenLines ?? [],
                laneLines: config.laneLines ?? [],
                originReferencePointLatLng: config.referencePoint ?? draft.facility.originReferencePointLatLng,
            } as any);
            replaceDraft({ ...draft, facility: nextFacility });
        },
        [draft, replaceDraft]
    );

    // ── Device actions ───────────────────────────────────────────────

    const addDevice = useCallback(() => {
        const index = (draft.facility.devices?.length ?? 0) + 1;
        const origin = draft.facility.originReferencePointLatLng?.origin;
        const device = new Device({
            id: `theoretical-device-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            name: `Theoretical Device ${index}`,
            positionX: origin?.x ?? draft.longitude ?? 0,
            positionY: origin?.y ?? draft.latitude ?? 0,
            headingDegrees: 0,
            desiredTiltDegrees: 0,
            height: 20,
            color: "#00E2FF",
        } as any);

        const nextFacility = new Facility({
            ...draft.facility,
            devices: [...(draft.facility.devices ?? []), device],
        } as any);
        replaceDraft({ ...draft, facility: nextFacility });
    }, [draft, replaceDraft]);

    const clearLayout = useCallback(() => {
        replaceDraft({
            ...draft,
            facility: createFacility({
                locationName: draft.locationName,
                latitude: draft.latitude,
                longitude: draft.longitude,
            }),
        });
        toastSuccess("Cleared all devices and layout items");
    }, [draft, replaceDraft]);

    // ── KML / KMZ import ─────────────────────────────────────────────

    const importKmlOrKmz = useCallback(
        async (file?: File) => {
            if (!file) return;
            try {
                const kmlText = await readKmlText(file);
                if (!kmlText) throw new Error("Could not read file contents.");

                const locations = parseKmlLocations(kmlText);
                if (!locations.length) {
                    toastError("No points found", "No point locations were found in this KML/KMZ.");
                    return;
                }

                const center = averageLatLon(locations);
                const oldHelper = getFacilityCoordsHelper(draft.facility);

                const toLatLng = <T,>(items: T[], getPoints: (item: T) => Vector2[]) =>
                    items.map((item) => ({
                        item,
                        points: getPoints(item).map((p) => oldHelper.toLatLng(p)),
                    }));

                const existingDevices = toLatLng(draft.facility.devices ?? [], (d) => [
                    new Vector2({ x: d.positionX ?? 0, y: d.positionY ?? 0 }),
                ]);
                const existingZones = toLatLng(draft.facility.detectionZones ?? [], (z) => z.points ?? []);
                const existingScreenLines = toLatLng(draft.facility.screenLines ?? [], (l) => l.points ?? []);
                const existingLaneLines = toLatLng(draft.facility.laneLines ?? [], (l) => l.points ?? []);

                const centeredFacility = createFacility({
                    locationName: draft.locationName,
                    latitude: center.latitude,
                    longitude: center.longitude,
                });
                const newHelper = getFacilityCoordsHelper(centeredFacility);

                const transformedDevices = existingDevices.map(({ item, points }) => {
                    const local = newHelper.fromLatLng(points[0]);
                    return new Device({ ...item, positionX: local.x, positionY: local.y } as any);
                });

                const transformItems = <T,>(
                    entries: { item: T; points: google.maps.LatLng[] }[],
                    Ctor: new (data: any) => T
                ) =>
                    entries.map(({ item, points }) => {
                        const localPoints = points.map((p) => newHelper.fromLatLng(p));
                        return new Ctor({ ...item, points: localPoints } as any);
                    });

                const importedDevices = locations.map((loc, idx) => {
                    const pos = newHelper.fromLatLng({ lat: loc.latitude, lng: loc.longitude });
                    return new Device({
                        id: `theoretical-device-import-${Date.now()}-${idx}`,
                        name: loc.name || `Imported Device ${transformedDevices.length + idx + 1}`,
                        positionX: pos.x,
                        positionY: pos.y,
                        headingDegrees: 0,
                        desiredTiltDegrees: 0,
                        height: 20,
                        color: "#00E2FF",
                    } as any);
                });

                const nextFacility = new Facility({
                    ...centeredFacility,
                    devices: [...transformedDevices, ...importedDevices],
                    detectionZones: transformItems(existingZones, DetectionZone),
                    screenLines: transformItems(existingScreenLines, ScreenLine),
                    laneLines: transformItems(existingLaneLines, LaneLine),
                } as any);

                replaceDraft({ ...draft, latitude: center.latitude, longitude: center.longitude, facility: nextFacility });
                toastSuccess(`Imported ${importedDevices.length} location${importedDevices.length === 1 ? "" : "s"} from ${file.name}`);
            } catch (error) {
                console.error("Failed to import KML/KMZ", error);
                toastError("Import failed", "Could not import the provided KML/KMZ file.");
            }
        },
        [draft, replaceDraft]
    );

    // ── PDF export ───────────────────────────────────────────────────

    const captureMap = useCallback(async () => {
        const root = mapContainerRef.current;
        if (!root) throw new Error("Export map is not initialized.");

        const mapNode = (root.querySelector(".gm-style")?.parentElement as HTMLElement) ?? root;
        const canvas = await html2canvas(mapNode, {
            backgroundColor: "#0f172a",
            useCORS: true,
            logging: false,
            scale: 2,
        });
        return canvas.toDataURL("image/jpeg", 0.95);
    }, []);

    const runExport = useCallback(async () => {
        if (!locationReady) {
            toastError("Set location first", "Enter location latitude and longitude before exporting.");
            return;
        }

        const config = designerRef.current?.getLayoutConfiguration() ?? toLayoutConfiguration(draft.facility);
        const devices = config.devices ?? [];
        if (!devices.length) {
            toastError("Add at least one device", "The PDF export needs one or more theoretical devices.");
            return;
        }

        setIsExporting(true);
        try {
            const coordsHelper = getFacilityCoordsHelper(draft.facility);
            const pdf = new jsPDF({ unit: "pt", format: "letter", orientation: "landscape" });
            const pageW = pdf.internal.pageSize.getWidth();
            const pageH = pdf.internal.pageSize.getHeight();
            const margin = 36;

            designerRef.current?.fitDevicesOnMap();
            await waitMs(900);
            const overviewImage = await captureMap();

            pdf.setFontSize(20);
            pdf.text(draft.locationName || "Sales Layout Plan", margin, 42);
            pdf.addImage(overviewImage, "JPEG", margin, 72, pageW - margin * 2, pageH - 72 - margin);

            for (let i = 0; i < devices.length; i++) {
                const latestConfig = designerRef.current?.getLayoutConfiguration() ?? config;
                const device = latestConfig.devices?.find((d) => d.id === devices[i].id) ?? devices[i];

                if (device.id) designerRef.current?.focusShapeById(device.id);
                designerRef.current?.setMapZoom(DEVICE_EXPORT_ZOOM);
                await waitMs(900);
                const image = await captureMap();

                const latLng = coordsHelper.toLatLng(new Vector2({ x: device.positionX ?? 0, y: device.positionY ?? 0 }));
                const heading = device.headingDegrees ?? 0;
                const tilt = device.desiredTiltDegrees ?? 0;
                const heightFt = (device.height ?? 0) * METERS_TO_FEET;
                const gap = 18;
                const detailsY = 42;
                const imageY = 136;

                pdf.addPage("letter", "landscape");
                pdf.setFontSize(18);
                pdf.text(`${draft.locationName || "Sales Layout Plan"} - Device ${i + 1}`, margin, detailsY);
                pdf.setFontSize(12);
                pdf.text(`Latitude: ${latLng.lat().toFixed(6)}`, margin, detailsY + gap);
                pdf.text(`Longitude: ${latLng.lng().toFixed(6)}`, margin, detailsY + gap * 2);
                pdf.text(`Heading: ${heading.toFixed(2)} deg`, margin, detailsY + gap * 3);
                pdf.text(`Height: ${heightFt.toFixed(2)} ft`, margin, detailsY + gap * 4);
                pdf.text(`Tilt: ${tilt.toFixed(2)} deg`, margin, detailsY + gap * 5);
                pdf.addImage(image, "JPEG", margin, imageY, pageW - margin * 2, pageH - imageY - margin);
            }

            pdf.save(`${sanitizeFileName(draft.locationName)}.pdf`);
            toastSuccess("PDF export complete");
        } catch (error) {
            console.error("Failed to export planner PDF", error);
            toastError("Export failed", "Could not generate PDF. Please try again.");
        } finally {
            setIsExporting(false);
            designerRef.current?.centerMap();
        }
    }, [captureMap, draft, locationReady]);

    // ── Render ───────────────────────────────────────────────────────

    return (
        <RequiresGoogleMaps>
            <div className="flex flex-col gap-4 p-5">
                <div className="border border-separators-page rounded p-4">
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <Input
                            label="Location Name"
                            value={draft.locationName}
                            placeholder="Downtown Northbound Approach"
                            onChange={(e) => updateLocation("locationName", e.currentTarget.value)}
                        />
                        <NumericInput label="Latitude" value={draft.latitude} onChange={(v) => updateLocation("latitude", v)} />
                        <NumericInput label="Longitude" value={draft.longitude} onChange={(v) => updateLocation("longitude", v)} />
                    </div>

                    <Input
                        label="Import KML / KMZ"
                        type="file"
                        accept=".kml,.kmz,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
                        onChange={(e) => importKmlOrKmz(e.currentTarget.files?.[0])}
                    />

                    {/* Wraps: the buttons are a fixed 40px tall, so three in one row on a phone
                        overflowed their own labels onto the help text underneath. */}
                    <div className="flex flex-wrap gap-2 mt-2">
                        <Button onClick={addDevice} disabled={!locationReady}>
                            <Icons.Plus className="inline mr-1 mb-0.5" /> Add Device
                        </Button>
                        <Button onClick={clearLayout} disabled={!hasAnyLayoutItems}>
                            <Icons.Trash className="inline mr-1 mb-0.5" /> Clear Layout
                        </Button>
                        <Button onClick={runExport} disabled={!locationReady || isExporting}>
                            <Icons.Download className="inline mr-1 mb-0.5" /> {isExporting ? "Exporting..." : "Export PDF"}
                        </Button>
                    </div>

                    <p className="text-sm text-gray-600 mt-2">Export uses the latest on-screen device and layout edits.</p>
                </div>

                <div className="border border-separators-page rounded p-3" ref={mapContainerRef}>
                    <h3 className="text-lg mb-3">{draft.locationName?.trim() || "Sales Layout Plan"}</h3>
                    <LayoutDesigner
                        ref={designerRef}
                        facility={draft.facility}
                        allowEdit
                        persistLayoutConfiguration={false}
                        onSaveLayoutConfiguration={onLayoutSaved}
                        hideWarning
                        mapMargins={{ bottom: 200, left: 0, right: 0, top: 0 }}
                    />
                </div>
            </div>
        </RequiresGoogleMaps>
    );
};
